import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { Translate, TextFormat, getSortState, JhiPagination, JhiItemCount, ValidatedField } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import { APP_DATE_FORMAT, APP_LOCAL_DATE_FORMAT } from 'app/config/constants';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './activity-time-availability.reducer';
import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { IActivityTimeAvailability, defaultValue } from 'app/shared/model/activity-time-availability.model';
import { IQueryParams } from '../../shared/reducers/reducer.utils';

export const ActivityTimeAvailability = () => {
  const dispatch = useAppDispatch();

  const location = useLocation();
  const navigate = useNavigate();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getSortState(location, ITEMS_PER_PAGE, 'id'), location.search)
  );

  const activityTimeAvailabilityList = useAppSelector(state => state.activityTimeAvailability.entities);
  const loading = useAppSelector(state => state.activityTimeAvailability.loading);
  const totalItems = useAppSelector(state => state.activityTimeAvailability.totalItems);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      })
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (location.search !== endURL) {
      navigate(`${location.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [location.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    } else {
      return order === ASC ? faSortUp : faSortDown;
    }
  };

  const apiUrl = 'api/activity-time-availabilities';
  const getEntitiesForActivity = createAsyncThunk(
    'activityTimeAvailability/fetch_entity_list',
    async ({ page, size, sort }: IQueryParams) => {
      const requestUrl = `${apiUrl}${
        sort ? `?page=${page}&size=${size}&sort=${sort}&activityId=${inputFilterValue}&` : '?'
      }cacheBuster=${new Date().getTime()}`;
      return axios.get<IActivityTimeAvailability[]>(requestUrl);
    }
  );
  const loadFilteredObjects = () => {
    dispatch(
      getEntitiesForActivity({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      })
    );
  };
  const [inputFilterValue, setInputFilterValue] = useState('');
  const handleInputChange = event => {
    const value = event.target.value;
    setInputFilterValue(value);
  };

  return (
    <div>
      <h2 id="activity-time-availability-heading" data-cy="ActivityTimeAvailabilityHeading">
        <Translate contentKey="activitiesApp.activityTimeAvailability.home.title">Activity Time Availabilities</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="activitiesApp.activityTimeAvailability.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link
            to="/activity-time-availability/new"
            className="btn btn-primary jh-create-entity"
            id="jh-create-entity"
            data-cy="entityCreateButton"
          >
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="activitiesApp.activityTimeAvailability.home.createLabel">
              Create new Activity Time Availability
            </Translate>
          </Link>
        </div>
      </h2>
      <div>
        <ValidatedField
          label="Filer for activity"
          id="activity-filter-text"
          name="toPax"
          data-cy="activityFilter"
          type="text"
          value={inputFilterValue}
          onChange={handleInputChange}
        />
        <Button className="me-2" color="info" onClick={loadFilteredObjects} disabled={loading}>
          Filter
        </Button>
      </div>
      <div className="table-responsive">
        {activityTimeAvailabilityList && activityTimeAvailabilityList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="activitiesApp.activityTimeAvailability.id">ID</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('fromDate')}>
                  <Translate contentKey="activitiesApp.activityTimeAvailability.fromDate">From Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('fromDate')} />
                </th>
                <th className="hand" onClick={sort('toDate')}>
                  <Translate contentKey="activitiesApp.activityTimeAvailability.toDate">To Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('toDate')} />
                </th>
                <th className="hand" onClick={sort('hour')}>
                  <Translate contentKey="activitiesApp.activityTimeAvailability.hour">Hour</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('hour')} />
                </th>
                <th className="hand" onClick={sort('minute')}>
                  <Translate contentKey="activitiesApp.activityTimeAvailability.minute">Minute</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('minute')} />
                </th>
                <th className="hand" onClick={sort('ampm')}>
                  <Translate contentKey="activitiesApp.activityTimeAvailability.ampm">Ampm</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('ampm')} />
                </th>
                <th className="hand" onClick={sort('adultCount')}>
                  <Translate contentKey="activitiesApp.activityTimeAvailability.adultCount">Adult Count</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('adultCount')} />
                </th>
                <th className="hand" onClick={sort('childCount')}>
                  <Translate contentKey="activitiesApp.activityTimeAvailability.childCount">Child Count</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('childCount')} />
                </th>
                <th className="hand" onClick={sort('infantCount')}>
                  <Translate contentKey="activitiesApp.activityTimeAvailability.infantCount">Infant Count</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('infantCount')} />
                </th>
                <th className="hand" onClick={sort('userName')}>
                  <Translate contentKey="activitiesApp.activityTimeAvailability.userName">User Name</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('userName')} />
                </th>
                <th>
                  <Translate contentKey="activitiesApp.activityTimeAvailability.activityAvailability">Activity Availability</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {activityTimeAvailabilityList.map((activityTimeAvailability, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/activity-time-availability/${activityTimeAvailability.id}`} color="link" size="sm">
                      {activityTimeAvailability.id}
                    </Button>
                  </td>
                  <td>
                    {activityTimeAvailability.fromDate ? (
                      <TextFormat type="date" value={activityTimeAvailability.fromDate} format={APP_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>
                    {activityTimeAvailability.toDate ? (
                      <TextFormat type="date" value={activityTimeAvailability.toDate} format={APP_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>{activityTimeAvailability.hour}</td>
                  <td>{activityTimeAvailability.minute}</td>
                  <td>
                    <Translate contentKey={`activitiesApp.AMPM.${activityTimeAvailability.ampm}`} />
                  </td>
                  <td>{activityTimeAvailability.adultCount}</td>
                  <td>{activityTimeAvailability.childCount}</td>
                  <td>{activityTimeAvailability.infantCount}</td>
                  <td>{activityTimeAvailability.userName}</td>
                  <td>
                    {activityTimeAvailability.activityAvailability ? (
                      <Link to={`/activity-availability/${activityTimeAvailability.activityAvailability.id}`}>
                        {activityTimeAvailability.activityAvailability.id}
                      </Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button
                        tag={Link}
                        to={`/activity-time-availability/${activityTimeAvailability.id}`}
                        color="info"
                        size="sm"
                        data-cy="entityDetailsButton"
                      >
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/activity-time-availability/${activityTimeAvailability.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/activity-time-availability/${activityTimeAvailability.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="activitiesApp.activityTimeAvailability.home.notFound">No Activity Time Availabilities found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={activityTimeAvailabilityList && activityTimeAvailabilityList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default ActivityTimeAvailability;
